/**
 * Kiracı yaşam döngüsü sözleşmesi (Castinflow v3.1 Dalga 1 B3–B5; ADR-0015 § 5–6; K-V3-05/06).
 *
 * Ayrı eksenler (birbirinin yerine geçmez):
 *  - DURUM (`tenants.status`): `suspended` = platform operatörünün askısı. Oturum, API anahtarı ve destek oturumu reddedilir
 *    (`TENANT_SUSPENDED`, 423); iş koşucusu dış etkili işi yürütmez; takvim/tarih tetikleri ve IMAP yoklaması üretilmez. Veri
 *    silinmez. Kapatma (`closing`/`closed`) müşterinin kendi akışıdır.
 *  - DENEME (`trial_ends_at`): yeni çalışma alanında oluşturma anı + 14 gün (SÜRE esaslı UTC anı). Süre bitince YAZMA kısıtı
 *    (`TRIAL_EXPIRED`, 403): okuma, arama, dışa aktarma, hesap kapatma ve oturum/güvenlik uçları açık; dış etkili iş yürümez.
 *    NULL = "süresi tanımsız eski deneme" (migration öncesi çalışma alanları; geriye dönük kapatılmaz). `lifecycle_exempt`
 *    çalışma alanı (demo, test) deneme kuralına girmez. Ücretli plan ataması denemeyi sonlandırır.
 *  - PLAN/KOTA: tavan aşımı yalnız ilgili oluşturmayı reddeder (`PLAN_LIMIT`, `STORAGE_FULL`); askı değildir.
 *
 * Uyarılar (7/3/1 gün) bitiş anından SÜRE olarak geriye sayılır (UTC); gösterim çalışma alanının IANA saat dilimindedir.
 */
import { z } from 'zod';
import { TENANT_STATUSES } from './data-lifecycle.js';
import { TENANT_TRIAL_STATES } from './ops-console.js';
import { DEFAULT_PLAN_ID, PLAN_LIMIT_KEYS, PLAN_SYSTEM_CAPS, planIdSchema } from './plans.js';
import { tenantSlugSchema } from './slug.js';
import { signupInputSchema } from './tenant.js';
const DAY_MS = 86_400_000;
const isoDateTime = z.iso.datetime({ offset: true });
const uuid = z.guid();
// ---------------------------------------------------------------------------------------------------------------------
// Deneme
// ---------------------------------------------------------------------------------------------------------------------
/** Yeni çalışma alanının deneme süresi (gün; süre esaslı — oluşturma anı + 14 × 24 saat) */
export const TRIAL_DAYS = 14;
/** Bitişten önce uyarı eşikleri (gün; süre esaslı) */
export const TRIAL_WARNING_DAYS = [7, 3, 1];
/** Uzatmada yeni bitiş en çok bu kadar gün ileride olabilir */
export const TRIAL_EXTENSION_MAX_DAYS = 180;
/** Deneme durumu (veritabanındaki envanter fonksiyonuyla aynı kural: muaf → deneme dışı plan → tanımsız → bitti → sürüyor) */
export function trialStateOf(t, now) {
    if (t.lifecycleExempt)
        return 'exempt';
    if (t.planId !== DEFAULT_PLAN_ID)
        return 'none';
    if (t.trialEndsAt === null)
        return 'legacy';
    return new Date(t.trialEndsAt).getTime() <= now.getTime() ? 'expired' : 'active';
}
/**
 * Uyarı eşiği: bitişe kalan SÜRE ≤ N gün olan en küçük eşik (7 → 3 → 1). Süre dolmuşsa ya da 7 günden fazla varsa `null`.
 * Takvim günü değildir: bitiş 10 Kasım 09:00 UTC ise 1 günlük uyarı 9 Kasım 09:00 UTC'de başlar (yerel saat ne olursa olsun).
 */
export function trialWarningDays(endsAt, now) {
    if (endsAt === null)
        return null;
    const left = new Date(endsAt).getTime() - now.getTime();
    if (left <= 0)
        return null;
    let out = null;
    for (const d of TRIAL_WARNING_DAYS)
        if (left <= d * DAY_MS)
            out = d;
    return out;
}
/** Uyarı eşiklerinin UTC anları (bitiş − 7/3/1 × 24 saat); yaz saati geçişinden bağımsızdır */
export function trialWarningInstants(endsAt) {
    const end = new Date(endsAt).getTime();
    return {
        7: new Date(end - 7 * DAY_MS),
        3: new Date(end - 3 * DAY_MS),
        1: new Date(end - 1 * DAY_MS),
    };
}
// ---------------------------------------------------------------------------------------------------------------------
// Kiracı tarafı: hesap durumu (`GET /account/status` — askıda da yanıt veren tek bilgi ucu)
// ---------------------------------------------------------------------------------------------------------------------
export const TENANT_LIFECYCLE_TEXT = {
    suspendedTitle: 'Hesabınız askıya alındı',
    suspended: 'Hesabınız askıya alındı. Castinflow ile iletişime geçin.',
    trialExpiredTitle: 'Deneme süreniz sona erdi',
    trialExpired: 'Deneme süreniz sona erdi. Kayıtlarınızı görüntüleyebilir, arayabilir ve dışa aktarabilirsiniz; yeni kayıt ve değişiklik için Castinflow ile iletişime geçin.',
};
export const accountStatusSchema = z
    .object({
    status: z.enum(TENANT_STATUSES),
    /** Çalışma alanının IANA saat dilimi (tarih gösterimi) */
    timezone: z.string(),
    trial: z
        .object({
        state: z.enum(TENANT_TRIAL_STATES),
        endsAt: isoDateTime.nullable(),
        warningDays: z.union([z.literal(7), z.literal(3), z.literal(1)]).nullable(),
        /** Yazma kısıtı uygulanıyor (deneme bitti ve kural açık) */
        writeRestricted: z.boolean(),
    })
        .strict(),
    /** Askı ya da deneme sonu bilgisi (Türkçe); yoksa null */
    message: z.string().nullable(),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Operatör konsolu: yaşam döngüsü işlemleri (B3), plan/kota (B4), destek erişimi (B5)
// ---------------------------------------------------------------------------------------------------------------------
export const SUSPENSION_REASON_CODES = [
    'odeme',
    'kotuye_kullanim',
    'guvenlik',
    'musteri_talebi',
    'diger',
];
export const SUSPENSION_REASON_LABELS = {
    odeme: 'Ödeme',
    kotuye_kullanim: 'Kötüye kullanım',
    guvenlik: 'Güvenlik',
    musteri_talebi: 'Müşteri talebi',
    diger: 'Diğer',
};
export const REACTIVATION_REASON_CODES = [
    'odeme_alindi',
    'sorun_giderildi',
    'musteri_talebi',
    'diger',
];
export const REACTIVATION_REASON_LABELS = {
    odeme_alindi: 'Ödeme alındı',
    sorun_giderildi: 'Sorun giderildi',
    musteri_talebi: 'Müşteri talebi',
    diger: 'Diğer',
};
/** Gerekçe metni (operatör denetimine yazılır; kiracıya gösterilmez) */
export const opsReasonTextSchema = z
    .string()
    .trim()
    .min(3, 'Gerekçe en az 3 karakter olmalı')
    .max(1000, 'Gerekçe en çok 1000 karakter olabilir')
    .refine((v) => !/\p{Cc}/u.test(v.replace(/[\n\t]/g, ' ')), 'Gerekçe denetim karakteri içeremez');
export const opsTenantProvisionInputSchema = z
    .object({
    slug: tenantSlugSchema,
    companyName: signupInputSchema.shape.companyName,
    legalName: signupInputSchema.shape.legalName,
    ownerEmail: signupInputSchema.shape.email,
    ownerFirstName: signupInputSchema.shape.firstName,
    ownerLastName: signupInputSchema.shape.lastName,
    note: z.string().trim().max(300, 'Not en çok 300 karakter olabilir').optional(),
})
    .strict();
export const opsTenantProvisionResultSchema = z
    .object({
    /** `false`: aynı adresle daha önce açılmış (aynı girdi); hiçbir şey yazılmadı */
    created: z.boolean(),
    tenant: z.object({ id: uuid, slug: z.string(), name: z.string() }).strict(),
    trialEndsAt: isoDateTime.nullable(),
    /** İlk yönetici daveti iş kaydı (worker davet e-postasını üretir; kuyruğa alınma teslim değildir) */
    invite: z.enum(['requested', 'existing']),
})
    .strict();
export const opsSuspendInputSchema = z
    .object({ reasonCode: z.enum(SUSPENSION_REASON_CODES), reasonText: opsReasonTextSchema })
    .strict();
export const opsReactivateInputSchema = z
    .object({ reasonCode: z.enum(REACTIVATION_REASON_CODES), reasonText: opsReasonTextSchema })
    .strict();
export const opsSuspendResultSchema = z
    .object({
    status: z.literal('suspended'),
    revokedSessions: z.number().int(),
    endedSupportSessions: z.number().int(),
})
    .strict();
export const opsReactivateResultSchema = z
    .object({
    status: z.literal('active'),
    /** Askı sırasında bekleyen (yürütülmemiş) dış etkili iş sayısı: hiçbiri kendiliğinden gönderilmez, incelemeye alınır */
    pendingEffects: z.number().int(),
})
    .strict();
export const opsTrialExtendInputSchema = z
    .object({ endsAt: isoDateTime, reasonText: opsReasonTextSchema })
    .strict();
export const opsTrialExtendResultSchema = z
    .object({
    previousEndsAt: isoDateTime.nullable(),
    endsAt: isoDateTime,
    /** Deneme bitmişti: süre dolduktan sonra bekleyen dış etkili işler incelemeye alınır */
    wasExpired: z.boolean(),
    pendingEffects: z.number().int(),
})
    .strict();
export const opsLifecycleExemptInputSchema = z
    .object({ exempt: z.boolean(), reasonText: opsReasonTextSchema })
    .strict();
export const opsLifecycleExemptResultSchema = z
    .object({ exempt: z.boolean(), changed: z.boolean() })
    .strict();
/** Kaynak bazında özel tavanlar (verilmeyen anahtarın özel tavanı kalkar — tam durum yazılır) */
export const planOverridesSchema = z
    .partialRecord(z.enum(PLAN_LIMIT_KEYS), z.number().int().min(0).max(Number.MAX_SAFE_INTEGER))
    .superRefine((v, ctx) => {
    for (const [k, n] of Object.entries(v)) {
        const cap = PLAN_SYSTEM_CAPS[k];
        if (cap !== null && typeof n === 'number' && n > cap)
            ctx.addIssue({
                code: 'custom',
                path: [k],
                message: `Sistem üst sınırı ${String(cap)} aşılamaz`,
            });
    }
});
export const opsPlanAssignInputSchema = z
    .object({
    planId: planIdSchema,
    overrides: planOverridesSchema.prefault({}),
    reasonText: opsReasonTextSchema,
})
    .strict();
const planStateSchema = z
    .object({ planId: z.string(), overrides: z.record(z.string(), z.number()) })
    .strict();
export const opsPlanAssignResultSchema = z
    .object({
    changed: z.boolean(),
    previous: planStateSchema,
    next: planStateSchema,
    /** Ücretli plana geçişle deneme sonlandı */
    trialConverted: z.boolean(),
})
    .strict();
export const opsInviteReissueResultSchema = z.object({ requested: z.literal(true) }).strict();
// ── Destek erişimi (B5): müşterinin verdiği kodla süreli, salt-okunur görünüm ──────────────────────────────────────────
/** Konsoldan açılan destek oturumunun kiracı izinde görünen operatör etiketi (operatör kimliği maskeli; ADR-0015 § 4) */
export const CONSOLE_SUPPORT_OPERATOR_LABEL = 'castinflow-operatoru';
export const MASKED_OPERATOR_NAME = 'Castinflow operatörü';
export const opsSupportRedeemInputSchema = z
    .object({ tenantId: uuid, code: z.string().trim().min(16).max(200) })
    .strict();
export const opsSupportSessionSchema = z
    .object({
    sessionId: uuid,
    tenant: z.object({ id: uuid, slug: z.string(), name: z.string() }).strict(),
    startedAt: isoDateTime,
    expiresAt: isoDateTime,
    scope: z.literal('read_only'),
})
    .strict();
/** Destek görünümünde listelenebilen kayıt tipleri (kiracı API'sinin salt-okunur liste ucu) */
export const SUPPORT_VIEW_RECORD_TYPES = [
    'company',
    'contact',
    'lead',
    'opportunity',
    'ticket',
    'activity',
];
export const opsSupportQueryInputSchema = z
    .object({
    type: z.enum(SUPPORT_VIEW_RECORD_TYPES),
    cursor: z.string().min(1).max(2048).nullable().default(null),
})
    .strict();
export const opsSupportRecordInputSchema = z
    .object({ type: z.enum(SUPPORT_VIEW_RECORD_TYPES), id: uuid })
    .strict();
//# sourceMappingURL=tenant-lifecycle.js.map